/* eslint-disable class-methods-use-this */
import moment from 'moment';
import magellan from 'magellan-coords';
import postfixCalculator from 'postfix-calculator';
import { Parser } from 'expr-eval';
import { VesselGlobal } from '../forms/types/FormDefinition';
import { Field } from '../fields/field';
import { FieldDependency } from '../fields/types/FieldDefinition';
import { extractIds, getPreviousGlobalValue } from './utils/helpers';
import {
  ComputerOutput,
  SubmissionGlobalValue,
} from './types/ComputerDefinition';
import haversine from './utils/haversine';

/**
 * The Computer class handles retrieving and calculating values based on string type formulas.
 * It can also be used for retrieving dependencies for a field based on these formulas.
 */
export class Computer {
  public fields: any;
  public globals: SubmissionGlobalValue[];
  public updatedGlobals: SubmissionGlobalValue[];
  public vesselGlobals: VesselGlobal[];
  public conversionTypes: string[];
  public date: Date | string;
  public previousSubmissionDate: Date | string;

  constructor({
    fields,
    globals,
    vesselGlobals,
    updatedGlobals,
    date = new Date(),
  }: {
    fields: Field[];
    globals: SubmissionGlobalValue[];
    vesselGlobals: VesselGlobal[];
    updatedGlobals: SubmissionGlobalValue[];
    date?: Date | string;
  }) {
    this.fields = fields;
    this.globals = globals;
    this.updatedGlobals = updatedGlobals;
    this.vesselGlobals = vesselGlobals;
    this.date = date;
    this.previousSubmissionDate = '';
    this.conversionTypes = [
      'calc',
      'latitudeDecimal',
      'longitudeDecimal',
      'unix',
      'equals',
      'visibleIf',
      'distance',
      'postfix',
    ];
  }

  /**
   * Set the date for the current submission as this can be used in formulas.
   */
  setDate(value: string): void {
    this.date = value;
  }

  /**
   * Set the date for the previous submission in relation to the current date as
   * this can be used in formulas.
   */
  setPreviousSubmissionDate(value: string): void {
    this.previousSubmissionDate = value;
  }

  /**
   * Extract a function type from a single formula string.
   */
  getFunction(formula: string): string {
    let conversion;
    this.conversionTypes.forEach((type) => {
      if (formula.indexOf(type) !== -1) {
        conversion = type;
      }
    });

    return conversion;
  }

  /**
   * Regular expression equations.
   */
  calc(formula: string): ComputerOutput | null {
    const parser = new Parser();

    const ids = extractIds({ formula, removePrefix: false });
    const formulaValues = this.getValuesFromFormula(formula);
    let valueBasedFormula = formula;

    // Replace ids within formula string with the corresponding field value.
    ids.forEach((id, index) => {
      valueBasedFormula = valueBasedFormula.replace(id, formulaValues[index]);
    });

    valueBasedFormula = valueBasedFormula.replace('calc', '');

    try {
      const expression = parser.parse(valueBasedFormula);
      const result = expression.evaluate();

      return {
        callback: 'setContent',
        value:
          Number.isNaN(result) || result === Infinity
            ? 0
            : Number.parseFloat(result).toFixed(2),
      };
    } catch (error) {
      console.error('Computer - calc - error', error);
      return null;
    }
  }

  /**
   * Compare different values and determine if they are equal.
   */
  equals(formula: string): ComputerOutput | null {
    const fieldValues = this.getValuesFromFormula(formula);

    return {
      value: fieldValues.every((value) => value === fieldValues[0]),
      callback: 'setEquals',
    };
  }

  /**
   * Calculate the visibility of a field.
   * This will only calculate based on one value per formula.
   * @fieldId[Value one,Value two]
   */
  visibleIf(formula: string): ComputerOutput | null {
    try {
      const [currentValue] = this.getValuesFromFormula(formula);

      // The potential values from within the formula made into an Array.
      const possibleValues = formula
        .substring(formula.lastIndexOf('[') + 1, formula.lastIndexOf(']'))
        .split(',');

      // Array.some will return true if one item meets the criteria
      // (if one potential is equal to the value).
      const value = possibleValues.some((item) => item === currentValue);

      return {
        value,
        callback: 'setVisibility',
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * From our Snapshot ancestor:
   *
   * Masters seems to enter a lot of positions using decimal places without a leading
   * zero eg: 12 12 .2 E rather than 12 12 0.2 E. This cause the magellan library to
   * miscalculate the lat/lon To stop this we need to replace any instance of a . without
   * a leading number with 0.
   *
   * @latitudeDecimal
   * Convert a set of latitude values to a single decimal value.
   */
  latitudeDecimal(formula: string): ComputerOutput {
    const fieldValues = this.getValuesFromFormula(formula).join(' ');
    const fixedFormula = fieldValues.replace(/\D\./, ' 0.');
    const latitude = magellan(fixedFormula).latitude();
    let decimal = latitude ? latitude.toDD() : '0';

    // magellan has a bug where it won't make the coordinate negative when the pole is
    // south and both the minutes and second = 0. This is a workaround:
    if (
      latitude
      && latitude.coordinate.direction === 'S'
      && Number(decimal) > 0
    ) {
      decimal = -decimal;
    }

    return {
      value: decimal,
      callback: 'setContent',
    };
  }

  /**
   * Convert a set of longitude values to a single decimal value.
   */
  longitudeDecimal(formula: string): ComputerOutput {
    const fieldValues = this.getValuesFromFormula(formula).join(' ');
    // Masters seems to enter a lot of positions using decimal places without a leading
    // zero eg: 12 12 .2 E rather than 12 12 0.2 E. This cause the magellan library to
    // mis calculate the lat/lon To stop this we need to replace any instance of a .
    // without a leading number with 0.
    const fixedFormula = fieldValues.replace(/\D\./, ' 0.');

    const longitude = magellan(fixedFormula).longitude();
    let decimal = longitude ? longitude.toDD() : '0';

    // magellan has a bug where it won't make the coordinate negative when the pole is
    // west and both the minutes and second = 0. This is a workaround:
    if (
      longitude
      && longitude.coordinate.direction === 'W'
      && Number(decimal) > 0
    ) {
      decimal = -decimal;
    }

    return {
      value: decimal,
      callback: 'setContent',
    };
  }

  /**
   * Function to convert multiple date and time fields
   * to become one unix timestamp.
   *
   * Values should be passed in the formula as (dateReference, timeReference, offsetReference)
   */
  unix(formula: string): ComputerOutput | null {
    const fieldValues = this.getValuesFromFormula(formula);
    const [initialDate] = fieldValues;

    // If we do not atleast have a valid date to start with, just return.
    if (!initialDate || initialDate === undefined || initialDate === '') {
      return null;
    }

    // Attempt to fetch correct values, otherwise fill time and offset with defaults.
    const unixParamters = [initialDate, '00:00', '+00:00'].map((value, index) => (!fieldValues[index]
      || fieldValues[index] === ''
      || fieldValues[index] === undefined
      ? value
      : fieldValues[index]));

    const [date, time, offset] = unixParamters;
    const dateOnly = date.includes('T') ? date.split('T')[0] : date;
    const dateToParse = `${moment(dateOnly).format('YYYY-MM-DD')} ${time} ${offset}`;
    const dateTimeUnix = moment.utc(dateToParse, 'YYYY-MM-DD HH:mm Z').unix();

    return {
      value: dateTimeUnix,
      callback: 'setContent',
    };
  }

  /**
   * Calculate distance in nautical miles between two lat lons.
   * The formula should be formatted as: `distance((lat1, long1), (lat2, long2))`
   */
  distance(formula: string) {
    const values = this.getValuesFromFormula(formula);

    // Make sure we've got the right values.
    if (!values || values.length < 4) {
      return null;
    }

    // If the last two values are 0 we can assume there was no previous
    // lat/lon. In that situation we should just return 0 for calculated
    // distance
    if (Number(values[2]) === 0 && Number(values[3]) === 0) {
      return {
        value: 0,
        callback: 'setContent',
      };
    }

    const distanceNM = haversine(
      { latitude: values[0], longitude: values[1] },
      { latitude: values[2], longitude: values[3] },
    );

    return {
      value: distanceNM.toFixed(),
      callback: 'setContent',
    };
  }

  /**
   * Run a `Postfix` type calculation using a dependency.
   */
  postfix(formula: string): ComputerOutput | null {
    const ids = extractIds({ formula, removePrefix: false });
    const formulaValues = this.getValuesFromFormula(formula).map((value) => (value === '' ? 0 : value));

    let valueBasedFormula = formula;

    // Replace ids within formula string with the corresponding field value.
    ids.forEach((id, index) => {
      valueBasedFormula = valueBasedFormula.replace(id, formulaValues[index]);
    });

    valueBasedFormula = valueBasedFormula.replace('postfix(', '');
    valueBasedFormula = valueBasedFormula.replace(')', '');

    // Borrowed from the later parts of https://medium.com/swlh/how-to-round-to-a-certain-number-of-decimal-places-in-javascript-ed74c471c1b8
    const roundAccurately = (number: number, decimalPlaces: number): number => Number(`${Math.round(Number(`${String(number)}e${decimalPlaces}`))}e-${decimalPlaces}`);

    const value = roundAccurately(postfixCalculator(valueBasedFormula), 2);

    return {
      value,
      callback: 'setContent',
    };
  }

  /**
   * Return an array of values from a formula string (value by field id).
   */
  getValuesFromFormula(formula: string): any[] {
    /**
     * Get all the values from the formula, but keep the prefix to determine what
     * type of value we should try and get.
     */

    const ids = extractIds({ formula, removePrefix: false });

    return ids.map((id) => {
      const type = id.charAt(0);

      // If the prefix is a `$` character, we know to go and get the previous global value.
      if (type === '$') {
        const global = getPreviousGlobalValue({
          globalId: id.slice(1),
          vesselGlobals: this.vesselGlobals,
          globalValues: this.globals,
          reportDate: this.date,
        });

        return global?.value ? global.value : '0';
      }

      // If the prefix is a `£` character, we will try and find this from the updated
      // globals in our current form
      if (type === '£') {
        return (
          this.updatedGlobals.find((global) => global.globalId === id.slice(1))
            ?.value || '0'
        );
      }

      // '#current' is used to access the form's date as a value.
      if (id === '#current') {
        return moment(this.date).unix();
      }

      // '#previous' is used to access the previous submission's date as a value.
      if (id === '#previous') {
        return moment(this.previousSubmissionDate).unix();
      }

      // Otherwise, lets just grab the main field id from within our current form.
      return (
        this.fields
          .find((field) => field.fieldDef.id === id.slice(1))
          .getContent() || '0'
      );
    });
  }

  /**
   * The primary computational method called which will delegate
   * formulas to functions and return these.
   */
  compute(formulas: string[]): (ComputerOutput | null)[] {
    const values: any[] = [];

    formulas.forEach((formula) => {
      const functionType = this.getFunction(formula);
      values.push(this[functionType](formula));
    });

    return values;
  }

  /**
   * Compute and return depencies (primary field ids) for a field.
   *
   * $ = A global reference from a value outside of the form.
   * £ = A global reference from within the same form.
   * @ = A single reference from within the same form.
   */
  getDependencies(formulas: string[]): FieldDependency[] {
    if (!formulas?.length) return [];
    const ids = formulas
      .map((formula) => extractIds({ formula, removePrefix: false }))
      .flat(1);

    const dependencies = ids
      .map((id) => {
        if (id.charAt(0) === '$') {
          return null;
        }

        if (id.charAt(0) === '£') {
          return {
            type: 'global',
            id: id.slice(1),
          };
        }

        return {
          type: 'local',
          id: id.slice(1),
        };
      })
      .filter((dependency) => dependency !== null) as FieldDependency[];

    return dependencies;
  }
}
